import React from "react"

import {
    CircularProgress,
    InputAdornment,
    MenuItem,
    MenuList,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import RoomIcon from "@material-ui/icons/Room"
import SearchIcon from "@material-ui/icons/Search"
import { GoogleMap, Marker } from "@react-google-maps/api"
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete"

// Selecao de endereco por busca do Google + mapa, compartilhada entre o cadastro de endereco
// do Registrador de Pedidos e a Edicao de Cliente. O componente so escolhe o ponto; os campos
// (numero, complemento...) ficam na tela de quem o usa.

// Converte um resultado do Geocoder do Google nos campos do endereco
// bairroAproximado: sem sublocality, usa o 1o componente "political" (comportamento da busca).
// No clique no mapa (zona rural) isso costuma trazer a propria cidade, entao fica vazio e editavel.
export const extrairEnderecoGoogle = (addr, { bairroAproximado = true } = {}) => {
    const get = (types, short = false) => {
        const c = addr.address_components?.find(ac => types.every(t => ac.types.includes(t)))
        return short ? c?.short_name : c?.long_name
    }

    return {
        rua: get(["route"]) || "",
        numero: get(["street_number"]) || "",
        bairro:
            get(["sublocality_level_1"]) ||
            get(["sublocality"]) ||
            (bairroAproximado ? get(["political"]) : "") ||
            "",
        cidade: get(["administrative_area_level_2"]) || get(["locality"]) || "",
        uf: get(["administrative_area_level_1"], true) || "",
        cep: get(["postal_code"], true) || "",
        lat: addr.geometry?.location?.lat?.(),
        lng: addr.geometry?.location?.lng?.(),
        formatted: addr.formatted_address,
    }
}

// O mapa depende do script do Google (GoogleMapsProvider no App)
export const googleMapsCarregado = () => !!window.google?.maps

// Icone da loja no mapa (icone "Store" do Material em um circulo azul)
const ICONE_LOJA_MAPA =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
            '<circle cx="20" cy="20" r="18" fill="#1976d2" stroke="#fff" stroke-width="3"/>' +
            '<g transform="translate(8 8)"><path fill="#fff" d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z"/></g>' +
            "</svg>"
    )

// Minimo de caracteres antes de consultar o Places. Com o debounce, reduz o numero
// de requisicoes de Autocomplete (cada predicao e cobrada por requisicao neste fluxo).
const MIN_CARACTERES_BUSCA = 5

const textoPontoNoMapa = (lat, lng) => `Ponto no mapa (${lat.toFixed(5)}, ${lng.toFixed(5)})`

const BuscaEnderecoGoogle = ({ onSelected, inputRef }) => {
    const {
        ready,
        value,
        setValue,
        suggestions: { loading, status, data },
        clearSuggestions,
    } = usePlacesAutocomplete({ debounce: 900 })

    // Cobre o intervalo do debounce: sem isso o usuario nao ve nada acontecendo e acha que travou
    const [aguardandoBusca, setAguardandoBusca] = React.useState(false)
    React.useEffect(() => {
        if (loading || status) setAguardandoBusca(false)
    }, [loading, status, data])

    const handleBuscar = e => {
        const texto = e.target.value
        const deveBuscar = texto.trim().length >= MIN_CARACTERES_BUSCA
        // 2o argumento = shouldFetchData: false apenas atualiza o input, sem chamar a API.
        setValue(texto, deveBuscar)
        setAguardandoBusca(deveBuscar)
        if (!deveBuscar) clearSuggestions()
    }

    const qtdCaracteres = value.trim().length
    const buscando = aguardandoBusca || loading
    let ajuda = "Digite a rua e o número e escolha uma das sugestões"
    let ajudaErro = false
    if (buscando) ajuda = "Buscando endereços…"
    else if (qtdCaracteres > 0 && qtdCaracteres < MIN_CARACTERES_BUSCA)
        ajuda = "Continue digitando…"
    else if (status === "ZERO_RESULTS") {
        ajuda = "Nenhum endereço encontrado — confira o nome da rua e a cidade"
        ajudaErro = true
    } else if (status === "OK" && data?.length) ajuda = "Escolha o endereço na lista"

    const handleSelect = async description => {
        try {
            setValue(description, false)
            setAguardandoBusca(false)
            clearSuggestions()
            const results = await getGeocode({ address: description })
            const addr = results?.[0]
            if (!addr) return
            onSelected(extrairEnderecoGoogle(addr))
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <div style={{ position: "relative" }}>
            <TextField
                value={value}
                onChange={handleBuscar}
                disabled={!ready}
                inputRef={inputRef}
                label="Endereço do cliente"
                placeholder="Ex.: Rua das Flores, 20"
                variant="outlined"
                fullWidth
                autoComplete="off"
                helperText={ajuda}
                error={ajudaErro}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    style: { background: "#fff" },
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                    endAdornment: buscando ? (
                        <InputAdornment position="end">
                            <CircularProgress size={20} />
                        </InputAdornment>
                    ) : null,
                }}
            />
            {status === "OK" && data?.length ? (
                <Paper style={{ position: "absolute", zIndex: 10000, width: "100%" }}>
                    <MenuList>
                        {data.map(({ place_id, description }) => (
                            <MenuItem
                                key={place_id}
                                value={description}
                                button
                                divider
                                onClick={() => handleSelect(description)}
                            >
                                {description}
                            </MenuItem>
                        ))}
                    </MenuList>
                </Paper>
            ) : null}
        </div>
    )
}

/**
 * Props:
 * - ponto: { lat, lng } escolhido (ou lat null)
 * - resumo: texto do endereco escolhido (exibido abaixo do mapa)
 * - onEscolher(place): endereco escolhido na busca ou por clique no mapa
 *   (campos de extrairEnderecoGoogle; no clique, numero vem vazio)
 * - onAjustarPonto({ lat, lng }): pino arrastado (so coordenadas)
 * - centroLoja: { lat, lng } do estabelecimento, ou null
 * - inputRef / chaveBusca: foco e reset do campo de busca
 * - onBuscandoPonto(bool): avisa enquanto o geocode reverso do clique roda
 */
export default function SelecaoEnderecoMapa({
    ponto,
    resumo,
    onEscolher,
    onAjustarPonto,
    centroLoja,
    inputRef,
    chaveBusca = 0,
    alturaMapa = 400,
    onBuscandoPonto = () => {},
}) {
    const [buscandoPonto, setBuscandoPonto] = React.useState(false)
    const temPonto = !!ponto?.lat

    const mapCenter = temPonto
        ? { lat: ponto.lat, lng: ponto.lng }
        : centroLoja || { lat: -12.720151, lng: -50.43879 }

    const marcarBuscandoPonto = valor => {
        setBuscandoPonto(valor)
        onBuscandoPonto(valor)
    }

    // Clique no mapa: marca o ponto sem digitar (zona rural / sem endereco). Faz 1 geocode
    // reverso para cidade/UF (e bairro/rua quando houver). O numero NAO vem do reverso
    // (seria aproximado): o atendente informa, ou "S/N".
    const escolherPontoNoMapa = async e => {
        const lat = e.latLng?.lat()
        const lng = e.latLng?.lng()
        if (!lat || !lng) return
        onAjustarPonto({ lat, lng })
        marcarBuscandoPonto(true)
        try {
            const results = await getGeocode({ location: { lat, lng } })
            const addr = results?.[0]
            const place = addr ? extrairEnderecoGoogle(addr, { bairroAproximado: false }) : {}
            onEscolher({
                ...place,
                numero: "",
                lat,
                lng,
                formatted: place.formatted || textoPontoNoMapa(lat, lng),
            })
        } catch (err) {
            // Sem retorno do Google (ex.: area sem endereco): fica so com as coordenadas
            onEscolher({ lat, lng, formatted: textoPontoNoMapa(lat, lng) })
        } finally {
            marcarBuscandoPonto(false)
        }
    }

    // Arrastar o pino ajusta so as coordenadas (os campos digitados continuam valendo)
    const pinoArrastado = e => {
        const lat = e.latLng?.lat()
        const lng = e.latLng?.lng()
        if (lat && lng) onAjustarPonto({ lat, lng })
    }

    return (
        <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Busque o endereço do cliente
            </Typography>
            <BuscaEnderecoGoogle key={chaveBusca} onSelected={onEscolher} inputRef={inputRef} />
            <Typography
                variant="body2"
                color="textSecondary"
                style={{ margin: "0.75rem 0 0.5rem" }}
            >
                Sem endereço (ex.: zona rural)? Clique no mapa para marcar o local.
            </Typography>
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    height: alturaMapa,
                    borderRadius: 8,
                    overflow: "hidden",
                }}
            >
                <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={mapCenter}
                    zoom={temPonto ? 17 : centroLoja ? 14 : 4}
                    onClick={escolherPontoNoMapa}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: true,
                        clickableIcons: false,
                        draggableCursor: "crosshair",
                    }}
                >
                    {/* Loja: icone de loja (diferente do pino), para nao parecer um endereco ja escolhido */}
                    {centroLoja ? (
                        <Marker
                            title="Estabelecimento"
                            clickable={false}
                            position={centroLoja}
                            icon={{
                                url: ICONE_LOJA_MAPA,
                                scaledSize: new window.google.maps.Size(40, 40),
                                anchor: new window.google.maps.Point(20, 20),
                            }}
                        />
                    ) : null}
                    {temPonto ? (
                        <Marker
                            title="Arraste para ajustar o local"
                            position={{ lat: ponto.lat, lng: ponto.lng }}
                            draggable
                            onDragEnd={pinoArrastado}
                        />
                    ) : null}
                </GoogleMap>
                {buscandoPonto ? (
                    <CircularProgress
                        size={32}
                        style={{ position: "absolute", top: 12, right: 60 }}
                    />
                ) : null}
            </div>
            <Typography
                variant="caption"
                color="textSecondary"
                component="div"
                style={{ marginTop: 4 }}
            >
                {temPonto
                    ? "Se o pino não estiver no local exato, arraste-o ou clique no ponto certo."
                    : centroLoja
                    ? "O ícone azul de loja é o estabelecimento."
                    : null}
            </Typography>
            {temPonto && resumo ? (
                <Paper
                    variant="outlined"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "10px 12px",
                        marginTop: 12,
                    }}
                >
                    <RoomIcon color="primary" />
                    <Typography variant="body2" style={{ flex: 1 }}>
                        {resumo}
                    </Typography>
                </Paper>
            ) : null}
        </div>
    )
}
